import type { MoveInSlotInfo } from '../../services/tenantApi';

/** Matches server sentinel for unlimited noon slots at 390 Johnson. */
export const JOHNSON_390_UNLIMITED_SLOT_CAPACITY = 9999;

/** Standard per-hour cap shown in the picker (matches server limited slots). */
export const JOHNSON_390_LIMITED_SLOT_CAPACITY = 5;

/** Hourly move-in slots shown in the 390 Johnson Calendly-style picker (9 AM – 6 PM ET). */
export const JOHNSON_390_APPOINTMENT_HOURS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18] as const;

/** One-time move-in blackout dates (Eastern). Keep in sync with shared johnson390-move-in-slots.ts. */
export const JOHNSON_390_MOVE_IN_BLOCKED_DATES_ET = ['2026-09-06', '2026-09-07'] as const;

export function isJohnson390MoveInDateBlocked(dateET: string): boolean {
  return (JOHNSON_390_MOVE_IN_BLOCKED_DATES_ET as readonly string[]).includes(dateET);
}

/** By-the-bed tenants cannot book before their assigned hour on their scheduled date; later dates allow any hour. */
export function isJohnson390BedLeaseBookableSlot(
  hour: number,
  dateStr: string,
  scheduledDateIso: string,
): boolean {
  const scheduledDateET = scheduledDateToEasternDate(scheduledDateIso);
  if (dateStr < scheduledDateET) return false;
  if (dateStr > scheduledDateET) return true;
  return hour >= scheduledDateToEasternHour(scheduledDateIso);
}

/** Collapsed default-noon label — visually matches other slots while discouraging noon. */
export function formatJohnson390DefaultNoonCollapsedAvailability(): string {
  return `0 of ${JOHNSON_390_LIMITED_SLOT_CAPACITY} available`;
}

export function scheduledDateToEasternDate(isoUtc: string): string {
  return new Date(isoUtc).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

export function scheduledDateToEasternHour(isoUtc: string): number {
  return parseInt(
    new Date(isoUtc).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false,
    }),
    10,
  );
}

export function findMoveInSlotForHour(
  slots: MoveInSlotInfo[] | undefined,
  hour: number,
): MoveInSlotInfo | undefined {
  if (!slots) return undefined;
  return slots.find((s) => {
    const etHour = parseInt(
      new Date(s.startTime).toLocaleTimeString('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        hour12: false,
      }),
      10,
    );
    return etHour === hour;
  });
}

/** Short demand stat for the tenant's default noon slot. */
export function formatJohnson390DefaultNoonDemandStat(appointmentCount: number): string {
  if (appointmentCount === 0) {
    return 'No groups booked at noon yet—most tenants still have this as their default time';
  }
  return `⚠️ ${appointmentCount} ${appointmentCount === 1 ? 'group is' : 'groups are'} currently expected at noon`;
}

/** Persuasive copy encouraging a different move-in hour. */
export function formatJohnson390DefaultNoonPitch(): string {
  return 'Noon is the busiest hour on move-in day. Expect lines at the check-in desk and longer waits for the elevators. An earlier (9–11 AM) or later (1–6 PM) slot will feature less congestion and a more pleasant move-in experience. We recommend shifting your move-in appointment to a different time.';
}

/** Full message for banners and compact sublabels. */
export function formatJohnson390DefaultMoveInTimeDemandMessage(appointmentCount: number): string {
  return `${formatJohnson390DefaultNoonDemandStat(appointmentCount)}. ${formatJohnson390DefaultNoonPitch()}`;
}

/** Sublabel for the 390 Johnson noon slot showing confirmed appointment demand. */
export function formatJohnson390NoonSlotSublabel(
  appointmentCount: number,
  opts: { isBooked: boolean; isDefault: boolean },
): string {
  const countPhrase = appointmentCount === 0
    ? 'no groups expected at this time yet'
    : `${appointmentCount} ${appointmentCount === 1 ? 'group' : 'groups'} expected at this time`;
  if (opts.isBooked) return `Your appointment · ${countPhrase}`;
  if (opts.isDefault) {
    return `Your default time · ${formatJohnson390DefaultNoonDemandStat(appointmentCount)}`;
  }
  return countPhrase;
}

/** Secondary lines under the default noon slot button. */
export function formatJohnson390DefaultNoonRecommendation(appointmentCount: number): {
  stat: string;
  pitch: string;
} {
  return {
    stat: formatJohnson390DefaultNoonDemandStat(appointmentCount),
    pitch: formatJohnson390DefaultNoonPitch(),
  };
}

/** Banner copy encouraging tenants to avoid the busy noon default slot. */
export function formatJohnson390NoonDemandBanner(appointmentCount: number): string {
  return `⚠️ ${formatJohnson390DefaultMoveInTimeDemandMessage(appointmentCount)}`;
}

/** Whether the current tenant is included in noon demand on the scheduled date. */
export function userCountsTowardJohnson390NoonDemand(opts: {
  userAppointmentStartTime: string | null;
  userScheduledDate: string;
  targetDateET: string;
  targetHour: number;
}): boolean {
  if (opts.userAppointmentStartTime) {
    return scheduledDateToEasternDate(opts.userAppointmentStartTime) === opts.targetDateET
      && scheduledDateToEasternHour(opts.userAppointmentStartTime) === opts.targetHour;
  }
  return scheduledDateToEasternDate(opts.userScheduledDate) === opts.targetDateET
    && scheduledDateToEasternHour(opts.userScheduledDate) === opts.targetHour;
}

/** Total expected noon demand on the scheduled date, including the current tenant when applicable. */
export function getJohnson390NoonAppointmentCount(
  noonSlot: MoveInSlotInfo | undefined,
  opts: {
    userAppointmentStartTime: string | null;
    userScheduledDate: string;
    targetDateET: string;
    targetHour: number;
  },
): number {
  if (!noonSlot) return 0;
  const userAtNoon = userCountsTowardJohnson390NoonDemand(opts);
  return noonSlot.booked + (userAtNoon ? 1 : 0);
}

function formatLocalDateYYYYMMDD(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Calendar week Mon–Sun containing `referenceDateEt`, shifted by `weekOffset` full weeks. */
export function buildMondaySundayWeek(referenceDateEt: string, weekOffset = 0): string[] {
  const [y, m, d] = referenceDateEt.split('-').map(Number);
  const ref = new Date(y, m - 1, d);
  const day = ref.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  const monday = new Date(ref);
  monday.setDate(ref.getDate() - daysFromMonday + weekOffset * 7);

  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const cursor = new Date(monday);
    cursor.setDate(monday.getDate() + i);
    dates.push(formatLocalDateYYYYMMDD(cursor));
  }
  return dates;
}

/** Neutral slot styling for the 390 Johnson picker (gray base, red reserved for selection accent). */
export function formatJohnson390SlotAvailabilityLabel(available: number, capacity: number): string {
  if (capacity >= JOHNSON_390_UNLIMITED_SLOT_CAPACITY) {
    return 'Open — no limit at this time';
  }
  if (available === 0) {
    return 'Full — no slots remaining';
  }
  return `${available} of ${capacity} available`;
}

export function formatJohnson390BookedSlotLabel(startTime: string, endTime: string): string {
  const start = new Date(startTime).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const end = new Date(endTime).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `Your confirmed move-in · ${start} – ${end} ET`;
}

export function getJohnson390SlotStyle(
  available: number,
  capacity: number,
): { bg: string; hoverBg: string; text: string; label: string } {
  const slotLabel = formatJohnson390SlotAvailabilityLabel(available, capacity);
  if (available === 0) {
    return { bg: 'bg-gray-50', hoverBg: '', text: 'text-gray-400', label: slotLabel };
  }
  return {
    bg: 'bg-white',
    hoverBg: 'hover:bg-gray-50',
    text: 'text-gray-600',
    label: slotLabel,
  };
}

export const CALENDAR_WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Count bookable hourly slots on a day (same rules as the time picker grid). */
export function countJohnson390AvailableSlotsForDay(
  dateStr: string,
  daySlots: MoveInSlotInfo[] | undefined,
  opts: {
    todayET: string;
    defaultDateET: string;
    scheduledDate?: string;
    isBlocked?: boolean;
    /** By-the-bed tenants may only book on their assigned scheduled date. */
    isByTheBed?: boolean;
  },
): number | null {
  if (opts.isBlocked || dateStr < opts.defaultDateET || dateStr < opts.todayET) {
    return null;
  }

  let count = 0;
  for (const hour of JOHNSON_390_APPOINTMENT_HOURS) {
    if (
      opts.isByTheBed
      && opts.scheduledDate
      && !isJohnson390BedLeaseBookableSlot(hour, dateStr, opts.scheduledDate)
    ) {
      continue;
    }
    const slot = findMoveInSlotForHour(daySlots, hour);
    if (slot && slot.available > 0) {
      count += 1;
    }
  }
  return count;
}

/** Short label for week-calendar day cells. */
export function formatJohnson390DayAvailabilityLabel(
  availableSlotCount: number | null,
  opts?: { loading?: boolean },
): string {
  if (opts?.loading) return '…';
  if (availableSlotCount === null) return '—';
  if (availableSlotCount === 0) return 'Full';
  return `${availableSlotCount} ${availableSlotCount === 1 ? 'slot' : 'slots'}`;
}
